import {FC, useEffect, useState} from 'react';
import {useCan, usePersona} from 'src/permissions/hooks';
import {Box, Grid, Typography} from '@mui/material';
import {CalendarMonthOutlined, InsightsOutlined} from '@mui/icons-material';
import {useIntl} from 'react-intl';
import dayjs from 'dayjs';
import {store} from '../../app/store';
import {preloaderChangeStatus} from '../../stores/common/commonSlice';
import {Sessions, TotalConsumption} from '../../components/icons/ReportCardIcons';
import {ReportCard} from '../../components/reports/ReportCard';
import {contractsApi, invoicesApi, reportServiceApi, transactionsApi} from '../../api';
import {useAppSelector, useBusinessId} from '../../app/hooks';
import {PageHeader} from '../../components/common/PageHeader';
import {DateBasisToggle} from 'src/components/common/DateBasisToggle';
import {DateFilterType} from 'src/components/common/TimeSelect/TimeSelect.types';
import {DateSelect} from '../../components/common/DateSelect';
import {KpiGrid} from '../EnergyConsumptionReport/components/KpiGrid';
import {BusinessKpiGrid} from './components/BusinessKpiGrid';
import {DatesType} from './Reports.types';

type ConsumptionQueryTrigger = ReturnType<typeof reportServiceApi.useLazyGetConsumptionQuery>[0];
type TransactionsQueryTrigger = ReturnType<typeof transactionsApi.useLazyGetTransactionsQuery>[0];

const getConsumptionData = async (
    getConsumptionReports: ConsumptionQueryTrigger,
    scopeFilters: {businessId?: number; companyId?: number; userId?: number},
    dateFrom: number | undefined,
    dateTo: number | undefined
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        return getConsumptionReports({
            dateFrom: dateFrom!,
            dateTo: dateTo!,
            ...scopeFilters,
        });
    } catch (error) {
        console.error(error);
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

const getSessionsCountData = async (
    trigger: TransactionsQueryTrigger,
    filter: {
        businessId?: number;
        companyId?: number;
        employeeId?: number;
        userId?: number;
    },
    dateFrom: number | undefined,
    dateTo: number | undefined,
    basis: DateFilterType
) => {
    try {
        store.dispatch(preloaderChangeStatus(true));
        return await trigger({
            params: {page: 0, size: 1},
            data: {
                ...filter,
                // Only the session count moves with this. Consumption comes from report-service, which
                // attributes energy to the moment it was measured and has no notion of which end of a
                // session a period should catch - which is exactly why the two figures differ, and why
                // the toggle is labelled for sessions rather than for the page.
                ...(basis === 'stop'
                    ? {stopFrom: dateFrom, stopTo: dateTo}
                    : {startFrom: dateFrom, startTo: dateTo}),
            },
        }).unwrap();
    } catch (error) {
        console.error(error);
    } finally {
        store.dispatch(preloaderChangeStatus(false));
    }
};

export const Reports: FC = () => {
    const intl = useIntl();
    const persona = usePersona();
    const {authInfo} = useAppSelector((state) => state.common);
    // Was a constant 1 for the platform administrator, in two places, because they have no
    // business of their own. It is now whichever one they are working in.
    const businessId = useBusinessId();
    const [getConsumptionReports, consumptionReports] =
        reportServiceApi.useLazyGetConsumptionQuery();
    const [getSessions, sessionsResult] = transactionsApi.useLazyGetTransactionsQuery();
    const [dateFilterType, setDateFilterType] = useState<DateFilterType>('start');
    const [getInvoicesSummary, invoicesSummary] = invoicesApi.useLazyGetInvoicesSummaryQuery();
    const [getContractsSummary, contractsSummary] = contractsApi.useLazyGetContractsSummaryQuery();

    const [dates, setDates] = useState<DatesType>({
        consumption: {
            from: dayjs.utc().startOf('year').valueOf(),
            to: dayjs.utc().valueOf(),
        },
        sessions: {
            from: dayjs.utc().startOf('year').valueOf(),
            to: dayjs.utc().valueOf(),
        },
    });
    const [currentTimeIndex, setCurrentTimeIndex] = useState<number>(3);

    // Single date handler — drives all widgets on the page.
    const handleChangeDates = (newFrom: number, newTo: number, _field: string) => {
        return () => {
            setDates({
                consumption: {from: newFrom, to: newTo},
                sessions: {from: newFrom, to: newTo},
            });
        };
    };

    const getSessionsFilter = () => {
        const authId = (authInfo as {id?: number})?.id;
        if (persona.isDriver || persona.isEmployee) {
            return {userId: authId};
        }
        return {
            businessId,
            companyId: authInfo?.company?.id,
            employeeId: authInfo?.employee?.id,
        };
    };

    const getConsumptionScope = (): {businessId?: number; companyId?: number; userId?: number} => {
        const authId = (authInfo as {id?: number})?.id;
        if (persona.isDriver || persona.isEmployee) {
            return {userId: authId};
        }
        // report-service takes businessId as an ordinary filter and clamps nothing for a caller who
        // reaches every tenant, so leaving it out is what made the consumption figures platform-wide
        // and identical whichever business was selected.
        return {businessId, companyId: authInfo?.company?.id};
    };

    const getBusinessScope = (): {businessId?: number; companyId?: number; userId?: number} => {
        const authId = (authInfo as {id?: number})?.id;
        if (persona.isDriver || persona.isEmployee) {
            return {userId: authId};
        }
        return {
            businessId,
            companyId: authInfo?.company?.id,
        };
    };

    const canReadSessions = useCan('transactions.search');
    // Business KPIs (revenue, MRR, contracts). This used to ask the persona, because the permission
    // was seeded to six roles and would have widened the block to four more - so the rule lived here
    // while POST /reports/revenue happily served anyone holding it. V1.172.0 narrowed the grant to the
    // owner and the platform admin, which is what the block always meant, and the screen can now ask
    // the same question the endpoint does.
    // reports.readRevenue says the revenue tiles are yours to see; it does not say the two endpoints
    // behind them are. A sales or technical manager holds it together with contracts.search and
    // without invoices.search, so the invoice half of this block used to 403 and come back empty.
    const canReadBusinessKpi = useCan('reports.readRevenue');
    // Called unconditionally, combined afterwards: a useCan behind && runs on some renders and not
    // others, which changes the hook count and takes the page down with it.
    const canSearchInvoices = useCan('invoices.search');
    const canSearchContracts = useCan('contracts.search');
    const canReadInvoiceKpi = canReadBusinessKpi && canSearchInvoices;
    const canReadContractKpi = canReadBusinessKpi && canSearchContracts;

    useEffect(() => {
        if (!authInfo) return;
        void getConsumptionData(
            getConsumptionReports,
            getConsumptionScope(),
            dates?.consumption?.from,
            dates?.consumption?.to
        );
        if (canReadInvoiceKpi || canReadContractKpi) {
            const scope = getBusinessScope();
            const createdFrom = dates?.consumption?.from;
            const createdTo = dates?.consumption?.to;
            if (canReadInvoiceKpi) {
                void getInvoicesSummary({
                    ...scope,
                    createdFrom,
                    createdTo,
                });
            }
            if (canReadContractKpi) {
                void getContractsSummary({
                    ...scope,
                    createdFrom,
                    createdTo,
                });
            }
        }
    }, [authInfo, dates, canReadInvoiceKpi, canReadContractKpi]);

    /**
     * The session count on its own, because it is the only figure the start/stop basis moves.
     *
     * Kept out of the effect above rather than added to its dependencies: consumption and the business
     * KPIs do not depend on the basis, and re-running all four would refetch three reports and show a
     * preloader every time somebody flips a control that cannot change them.
     */
    useEffect(() => {
        if (!authInfo || !canReadSessions) return;
        void getSessionsCountData(
            getSessions,
            getSessionsFilter(),
            dates?.sessions?.from,
            dates?.sessions?.to,
            dateFilterType
        );
    }, [authInfo, dates, dateFilterType, canReadSessions]);

    const reportsList = [
        {
            title: intl.formatMessage({id: 'energyConsumption'}),
            key: 'consumption',
            icon: <TotalConsumption/>,
            link: '/app/reports/energy-consumption',
            params: [
                {
                    amount: consumptionReports?.data
                        ? (consumptionReports.data.consumption / 1000).toFixed(2)
                        : 0,
                    description: intl.formatMessage({id: 'energyConsumption'}),
                    units: intl.formatMessage({id: 'kWh'}),
                },
            ],
        },
        ...(canReadSessions ? [{
            title: intl.formatMessage({id: 'sessions'}),
            key: 'sessions',
            icon: <Sessions/>,
            link: '/app/reports/charging-sessions',
            params: [
                {
                    amount: sessionsResult?.data?.page?.totalElements ?? 0,
                    description: intl.formatMessage({id: 'chargingSessions'}),
                },
            ],
        }] : []),
    ];

    return (
        <Box>
            <PageHeader
                title={intl.formatMessage({id: 'statistics'})}
                onlyTitle
                icon={<InsightsOutlined/>}
            >
                <Box sx={{display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, minWidth: 240}}>
                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5, color: 'neutral.500'}}>
                        <CalendarMonthOutlined sx={{fontSize: 18}}/>
                        <Typography sx={{fontSize: '13px', fontWeight: 500}}>
                            {intl.formatMessage({id: 'period'})}
                        </Typography>
                    </Box>
                    <Box sx={{flex: 1, minWidth: 180}}>
                        <DateSelect
                            handleTimeChange={handleChangeDates}
                            field="consumption"
                            currentTimeInit={currentTimeIndex}
                            handleSaveCurrentTimeIndex={setCurrentTimeIndex}
                            dense
                        />
                    </Box>
                    {canReadSessions && (
                        <DateBasisToggle
                            value={dateFilterType}
                            onChange={setDateFilterType}
                            withConsumptionCaveat
                        />
                    )}
                </Box>
            </PageHeader>
            <Box sx={{mb: 2}}>
                <KpiGrid
                    data={consumptionReports?.data}
                    loading={consumptionReports?.isFetching}
                />
            </Box>
            {(canReadInvoiceKpi || canReadContractKpi) && (
                <Box sx={{mb: 2}}>
                    <BusinessKpiGrid
                        invoices={invoicesSummary?.data}
                        contracts={contractsSummary?.data}
                        loading={invoicesSummary?.isFetching || contractsSummary?.isFetching}
                    />
                </Box>
            )}
            <Grid
                container
                rowSpacing={2}
                columnSpacing={{xs: 0, md: 2}}
            >
                {reportsList.map((report) => (
                    <Grid
                        key={report.title}
                        item
                        xs={12}
                        lg={6}
                    >
                        <ReportCard
                            icon={report.icon}
                            title={report.title}
                            params={report.params}
                            link={report.link}
                            preset={currentTimeIndex}
                        />
                    </Grid>
                ))}
            </Grid>
        </Box>
    );
};